import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Plus, Edit, Trash2, Send } from 'lucide-react';

export default function AdminSettings() {
  const DEFAULT_SMTP_PORT = 587;
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [salespersons, setSalespersons] = useState([]);
  const [costItems, setCostItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [emailConfig, setEmailConfig] = useState({ email: '', enabled: true });
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formType, setFormType] = useState('salesperson');

  const [smtpConfig, setSmtpConfig] = useState({ host: '', port: DEFAULT_SMTP_PORT, username: '', password: '', from_email: '' });
  const [smtpTesting, setSmtpTesting] = useState(false);

  const [salespersonForm, setSalespersonForm] = useState({ name: '' });
  const [costItemForm, setCostItemForm] = useState({ name: '', cost: 0, order: 0 });
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ name: '', email: '', password: '', role: 'user' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [salespersonsRes, costItemsRes, emailConfigRes, usersRes, smtpRes] = await Promise.all([
        axios.get(`${API}/salespersons`),
        axios.get(`${API}/cost-items`),
        axios.get(`${API}/email-config`),
        user?.role === 'admin' ? axios.get(`${API}/users`) : Promise.resolve({ data: [] }),
        user?.role === 'admin' ? axios.get(`${API}/smtp-config`).catch(() => ({ data: {} })) : Promise.resolve({ data: {} })
      ]);
      setSalespersons(salespersonsRes.data);
      setCostItems(costItemsRes.data);
      setEmailConfig(emailConfigRes.data);
      setUsers(usersRes.data);
      if (smtpRes.data) setSmtpConfig({ ...smtpConfig, ...smtpRes.data });
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const openDialog = (type, item = null) => {
    setFormType(type);
    setEditItem(item);
    if (type === 'salesperson') {
      setSalespersonForm(item ? { name: item.name } : { name: '' });
    } else {
      setCostItemForm(item ? { name: item.name, cost: item.cost, order: item.order } : { name: '', cost: 0, order: costItems.length + 1 });
    }
    setDialogOpen(true);
  };

  const handleSaveSalesperson = async () => {
    setLoading(true);
    try {
      if (editItem) {
        await axios.put(`${API}/salespersons/${editItem.id}`, salespersonForm);
        toast.success('Salesperson updated');
      } else {
        await axios.post(`${API}/salespersons`, salespersonForm);
        toast.success('Salesperson added');
      }
      fetchData();
      setDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save salesperson');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCostItem = async () => {
    setLoading(true);
    try {
      const payload = {
        ...costItemForm,
        cost: parseFloat(costItemForm.cost),
        order: parseInt(costItemForm.order)
      };
      if (editItem) {
        await axios.put(`${API}/cost-items/${editItem.id}`, payload);
        toast.success('Cost item updated');
      } else {
        await axios.post(`${API}/cost-items`, payload);
        toast.success('Cost item added');
      }
      fetchData();
      setDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save cost item');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      if (type === 'salesperson') {
        await axios.delete(`${API}/salespersons/${id}`);
        toast.success('Salesperson deleted');
      } else {
        await axios.delete(`${API}/cost-items/${id}`);
        toast.success('Cost item deleted');
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleSaveEmailConfig = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/email-config`, emailConfig);
      toast.success('Email configuration saved');
    } catch (error) {
      toast.error('Failed to save email configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSmtpConfig = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/smtp-config`, smtpConfig);
      toast.success('SMTP configuration saved');
    } catch (error) {
      toast.error('Failed to save SMTP configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleTestSmtp = async () => {
    setSmtpTesting(true);
    try {
      const res = await axios.post(`${API}/smtp-config/test`);
      toast.success(res.data.message);
    } catch (error) {
      const msg = error.response?.data?.detail || 'SMTP test failed';
      toast.error(typeof msg === 'string' ? msg : 'SMTP test failed');
    } finally {
      setSmtpTesting(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole });
      toast.success('User role updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleAddUser = async () => {
    if (!newUserForm.name || !newUserForm.email || !newUserForm.password) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users`, newUserForm);
      toast.success('User created successfully');
      setUserDialogOpen(false);
      setNewUserForm({ name: '', email: '', password: '', role: 'user' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-settings-page">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-10 backdrop-blur-md bg-white/80">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/')}
            data-testid="admin-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-slate-900 ml-4">Admin Settings</h1>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto p-6 md:p-8">
        <Tabs defaultValue="salespersons" className="space-y-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="salespersons" data-testid="salespersons-tab">Salespersons</TabsTrigger>
            <TabsTrigger value="cost-items" data-testid="cost-items-tab">Fixed Cost Items</TabsTrigger>
            {user?.role === 'admin' && <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>}
            <TabsTrigger value="email-config" data-testid="email-config-tab">Email Configuration</TabsTrigger>
          </TabsList>

          <TabsContent value="salespersons">
            <Card className="shadow-sm border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Salespersons</CardTitle>
                  <CardDescription>Manage salesperson list for job assignments</CardDescription>
                </div>
                <Button 
                  className="bg-slate-900 hover:bg-slate-800"
                  onClick={() => openDialog('salesperson')}
                  data-testid="add-salesperson-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Salesperson
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salespersons.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} className="text-center text-slate-600">
                          No salespersons added yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      salespersons.map((person) => (
                        <TableRow key={person.id} data-testid={`salesperson-row-${person.id}`}>
                          <TableCell className="font-medium">{person.name}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDialog('salesperson', person)}
                                data-testid={`edit-salesperson-${person.id}-btn`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete('salesperson', person.id)}
                                data-testid={`delete-salesperson-${person.id}-btn`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="cost-items">
            <Card className="shadow-sm border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Fixed Cost Items</CardTitle>
                  <CardDescription>Manage the 6 fixed cost line items that appear in every job</CardDescription>
                </div>
                <Button 
                  className="bg-slate-900 hover:bg-slate-800"
                  onClick={() => openDialog('cost-item')}
                  data-testid="add-cost-item-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Cost Item
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Item Name</TableHead>
                      <TableHead>Default Cost (AED)</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {costItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-slate-600">
                          No cost items added yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      costItems.map((item) => (
                        <TableRow key={item.id} data-testid={`cost-item-row-${item.id}`}>
                          <TableCell className="font-medium">{item.order}</TableCell>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>AED {item.cost.toFixed(2)}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDialog('cost-item', item)}
                                data-testid={`edit-cost-item-${item.id}-btn`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete('cost-item', item.id)}
                                data-testid={`delete-cost-item-${item.id}-btn`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {user?.role === 'admin' && (
            <TabsContent value="users">
              <Card className="shadow-sm border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>User Management</CardTitle>
                    <CardDescription>Manage user accounts and roles</CardDescription>
                  </div>
                  <Button 
                    onClick={() => setUserDialogOpen(true)}
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid="add-user-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-slate-600">
                            No users found
                          </TableCell>
                        </TableRow>
                      ) : (
                        users.map((u) => (
                          <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                            <TableCell className="font-medium">{u.name}</TableCell>
                            <TableCell>{u.email}</TableCell>
                            <TableCell>
                              <select
                                value={u.role}
                                onChange={(e) => handleRoleChange(u.id, e.target.value)}
                                className="border border-slate-300 rounded px-2 py-1"
                                disabled={u.id === user?.id}
                                data-testid={`user-role-${u.id}`}
                              >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                              </select>
                            </TableCell>
                            <TableCell>{new Date(u.created_at).toLocaleDateString()}</TableCell>
                            <TableCell>
                              {u.id !== user?.id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteUser(u.id)}
                                  data-testid={`delete-user-${u.id}-btn`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Add User Dialog */}
              <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                <DialogContent data-testid="add-user-dialog">
                  <DialogHeader>
                    <DialogTitle>Add New User</DialogTitle>
                    <DialogDescription>Create a new user account with a specific role</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="new-user-name">Name <span className="text-red-500">*</span></Label>
                      <Input
                        id="new-user-name"
                        value={newUserForm.name}
                        onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })}
                        placeholder="Enter name"
                        data-testid="new-user-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-user-email">Email <span className="text-red-500">*</span></Label>
                      <Input
                        id="new-user-email"
                        type="email"
                        value={newUserForm.email}
                        onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                        placeholder="Enter email"
                        data-testid="new-user-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-user-password">Password <span className="text-red-500">*</span></Label>
                      <Input
                        id="new-user-password"
                        type="password"
                        value={newUserForm.password}
                        onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                        placeholder="Enter password"
                        data-testid="new-user-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-user-role">Role</Label>
                      <select
                        id="new-user-role"
                        value={newUserForm.role}
                        onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })}
                        className="w-full border border-slate-300 rounded px-3 py-2"
                        data-testid="new-user-role-select"
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setUserDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleAddUser} 
                      disabled={loading}
                      className="bg-slate-900 hover:bg-slate-800"
                      data-testid="save-user-btn"
                    >
                      {loading ? 'Creating...' : 'Create User'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </TabsContent>
          )}

          <TabsContent value="email-config">
            <Card className="shadow-sm border-slate-200">
              <CardHeader>
                <CardTitle>Email Configuration</CardTitle>
                <CardDescription>Configure email address to receive job submission PDFs</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Recipient Email</h3>
                  <div className="space-y-2">
                    <Label htmlFor="config-email">Email Address to receive job PDFs</Label>
                    <Input
                      id="config-email"
                      type="email"
                      value={emailConfig.email}
                      onChange={(e) => setEmailConfig({ ...emailConfig, email: e.target.value })}
                      placeholder="email@example.com"
                      data-testid="email-config-input"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="email-enabled"
                      checked={emailConfig.enabled}
                      onChange={(e) => setEmailConfig({ ...emailConfig, enabled: e.target.checked })}
                      className="rounded border-slate-300"
                      data-testid="email-enabled-checkbox"
                    />
                    <Label htmlFor="email-enabled" className="font-normal cursor-pointer">
                      Enable automatic email notifications on job submission
                    </Label>
                  </div>
                </div>

                <div className="border-t pt-6 space-y-4">
                  <h3 className="text-lg font-semibold">SMTP Configuration</h3>
                  <p className="text-sm text-slate-500">Configure your SMTP server to enable email notifications and password reset emails.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="smtp-host">SMTP Host</Label>
                      <Input
                        id="smtp-host"
                        value={smtpConfig.host}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, host: e.target.value })}
                        placeholder="smtp.gmail.com"
                        data-testid="smtp-host-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="smtp-port">SMTP Port</Label>
                      <Input
                        id="smtp-port"
                        type="number"
                        value={smtpConfig.port}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, port: parseInt(e.target.value) || DEFAULT_SMTP_PORT })}
                        placeholder={String(DEFAULT_SMTP_PORT)}
                        data-testid="smtp-port-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="smtp-username">SMTP Username</Label>
                      <Input
                        id="smtp-username"
                        value={smtpConfig.username}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, username: e.target.value })}
                        placeholder="your-email@gmail.com"
                        data-testid="smtp-username-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="smtp-password">SMTP Password</Label>
                      <Input
                        id="smtp-password"
                        type="password"
                        value={smtpConfig.password}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, password: e.target.value })}
                        placeholder="App password"
                        data-testid="smtp-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="smtp-from-email">From Email</Label>
                      <Input
                        id="smtp-from-email"
                        type="email"
                        value={smtpConfig.from_email}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, from_email: e.target.value })}
                        placeholder="noreply@yourcompany.com"
                        data-testid="smtp-from-email-input"
                      />
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
                    For Gmail, generate an App Password at: <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline font-medium">Google App Passwords</a>
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      className="bg-slate-900 hover:bg-slate-800"
                      onClick={handleSaveSmtpConfig}
                      disabled={loading}
                      data-testid="save-smtp-config-btn"
                    >
                      {loading ? 'Saving...' : 'Save SMTP Settings'}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={handleTestSmtp}
                      disabled={smtpTesting}
                      data-testid="test-smtp-btn"
                    >
                      {smtpTesting ? 'Sending...' : 'Send Test Email'}
                    </Button>
                  </div>
                </div>

                <Button 
                  className="bg-slate-900 hover:bg-slate-800"
                  onClick={handleSaveEmailConfig}
                  disabled={loading}
                  data-testid="save-email-config-btn"
                >
                  {loading ? 'Saving...' : 'Save Configuration'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="admin-dialog">
          <DialogHeader>
            <DialogTitle>
              {editItem ? 'Edit' : 'Add'} {formType === 'salesperson' ? 'Salesperson' : 'Cost Item'}
            </DialogTitle>
          </DialogHeader>
          
          {formType === 'salesperson' ? (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="salesperson-name">Name</Label>
                <Input
                  id="salesperson-name"
                  value={salespersonForm.name}
                  onChange={(e) => setSalespersonForm({ name: e.target.value })}
                  data-testid="salesperson-name-input"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="item-name">Item Name</Label>
                <Input
                  id="item-name"
                  value={costItemForm.name}
                  onChange={(e) => setCostItemForm({ ...costItemForm, name: e.target.value })}
                  data-testid="cost-item-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="item-cost">Default Cost (AED)</Label>
                <Input
                  id="item-cost"
                  type="number"
                  step="0.01"
                  value={costItemForm.cost}
                  onChange={(e) => setCostItemForm({ ...costItemForm, cost: e.target.value })}
                  data-testid="cost-item-cost-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="item-order">Order</Label>
                <Input
                  id="item-order"
                  type="number"
                  value={costItemForm.order}
                  onChange={(e) => setCostItemForm({ ...costItemForm, order: e.target.value })}
                  data-testid="cost-item-order-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="dialog-cancel-btn">
              Cancel
            </Button>
            <Button 
              className="bg-slate-900 hover:bg-slate-800" 
              onClick={formType === 'salesperson' ? handleSaveSalesperson : handleSaveCostItem}
              disabled={loading}
              data-testid="dialog-save-btn"
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}